<template>
  <div
    class="gt-productpicker-plugin gt-productpicker-bycondition"
    v-show="master && master.activePlugin === id"
  >
    <el-form
      :model="form"
      ref="form"
      :rules="rules"
      :label-width="master && master.labelWidth"
      :size="master && master.formSize"
      :disabled="master && master.disabled"
    >
      <el-form-item
        :label="$t('Root.Picker.Select_ORG')"
        prop="orgCode"
        v-if="orgMultipleSupport"
      >
        <GtOrgTree
          v-model="form.orgCode"
          @change="onOrgChange"
          :disabled="master && master.disabled"
          custom-class="gt-productpicker-bycondition__org-picker"
        />
      </el-form-item>

      <div>
        <!-- brands -->
        <el-form-item prop="brands" :label="$t('Root.Picker.Brands')">
          <el-select
            class="gt-common--reset"
            v-model="form.brands"
            multiple
            filterable
            reserve-keyword
            v-loadmore="LoadMore"
            @visible-change="visibleSelect"
            clearable
            :placeholder="$t('Root.Basic.Select')"
          >
            <el-option
              v-for="(item, index) in brandListAll"
              :key="item.value + index"
              :label="item.label"
              :value="item.value"
            >
            </el-option>
          </el-select>
        </el-form-item>

        <!-- category -->
        <el-form-item :label="$t('Root.Picker.Categories')" prop="categories">
          <el-cascader-panel
            v-model="form.categories"
            :options="catgoryList"
            :props="{ multiple: true }"
          ></el-cascader-panel>
        </el-form-item>

        <!-- attribute -->
        <el-form-item>
          <!-- Label -->
          <div class="productpicker-label-notes" slot="label">
            <gt-smart-select
              v-if="attrMultipleSupport"
              v-model="form.attrType"
              size="mini"
              @change="attributeTypeToggle"
              :options="[
                { label: $t('Root.Picker.skuAttributes'), value: 'sku' },
                {
                  label: $t('Root.Picker.spuAttributes'),
                  value: 'spu'
                }
              ]"
            />

            <div v-else style="display: flex; align-items: center">
              <span>{{
                form.attrType === "spu"
                  ? $t("Root.Picker.spuAttributes")
                  : $t("Root.Picker.skuAttributes")
              }}</span>
              <gt-icon-tooltip
                :content="$t('Root.Picker.Only_select_attribut')"
              />
            </div>
          </div>

          <!-- content -->
          <div class="gt-productpicker-bycondition__attr-box">
            <p class="gt-productpicker-search-tips">
              {{
                $t("Root.Picker.Only_select_up_to_th", {
                  max:
                    form.attrType === "spu"
                      ? maxSPUAttributes
                      : maxSKUAttributes
                })
              }}
            </p>
            <ul>
              <li
                class="gt-productpicker-bycondition__attr-item"
                v-for="(item, idx) in form.attributes"
                :key="idx"
              >
                <el-form-item
                  :prop="`attributes.${idx}.id`"
                  :rules="[rules.attributes, rules.required]"
                >
                  <gt-smart-select
                    v-model="item.id"
                    :options="
                      form.attrType === 'sku' ? skuAttrList : spuAttrList
                    "
                    :placeholder="$t('Root.Picker.Select_attribute')"
                    @change="onAttrChange($event, idx)"
                  />
                </el-form-item>

                <el-form-item
                  :prop="`attributes.${idx}.values`"
                  :rules="rules.attributesValue"
                  :class="{
                    'gt-productpicker-bycondition__attr-item--hidden': !item.id
                  }"
                >
                  <gt-smart-select
                    v-model="item.values"
                    :options="attrValuesMap[item.id] || []"
                    multiple
                    clearable
                    filterable
                    :placeholder="$t('Root.Basic.Select')"
                  />
                </el-form-item>

                <span>
                  <el-button
                    type="text"
                    @click="deleteAttrItem(idx)"
                    icon="el-icon-delete"
                  />
                </span>
              </li>
              <li class="gt-productpicker-bycondition__attr-item">
                <el-button
                  :disabled="form.attributes.length >= maxSKUAttributes"
                  icon="el-icon-plus"
                  type="text"
                  @click="addAttrItem"
                  >{{ $t("Root.Picker.Add_attribute") }}
                </el-button>
              </li>
            </ul>
          </div>
        </el-form-item>
      </div>
    </el-form>
  </div>
</template>

<script>
import merge from "../mixins/merge.js";
import { mapActions, mapMutations, mapState } from "vuex";

export default {
  name: "ByCondition",

  mixins: [merge],

  props: {
    attrMultipleSupport: {
      type: Boolean,
      default: false
    },

    orgMultipleSupport: {
      type: Boolean,
      default: false
    },

    maxSKUAttributes: {
      type: Number,
      default: 3
    },

    maxSPUAttributes: {
      type: Number,
      default: 3
    }
  },

  computed: {
    ...mapState("root/productPicker", {
      brandList: state => state.brandList,
      skuAttrList: state => state.skuAttrList,
      spuAttrList: state => state.spuAttrList,
      attrValuesMap: state => state.attrValuesMap
    })
  },

  mounted() {
    this.dataInit();
  },

  data() {
    let that = this;

    return {
      form: {
        orgCode: "default",
        brands: [],
        categories: [],
        attrType: "sku", // sku or spu
        attributes: []
      },

      catgoryList: [],

      rules: {
        required: {
          message: this.$t("Root.Picker.Required"),
          required: true
        },

        attributes: {
          trigger: "change",
          validator(rule, value, callback) {
            let $form = that.$refs.form;

            that.form.attributes.forEach(({ id }, index) => {
              if (!id) return;

              let { length } = that.form.attributes.filter(_ => _.id === id);

              if (length > 1) {
                callback(new Error(that.$t("Root.Picker.Repeat")));
              } else {
                $form.clearValidate(`attributes.${index}.id`);
              }
            });

            callback();
          }
        },

        attributesValue: {
          message: this.$t("Root.Picker.Please_select_at_least_attribute"),
          required: true
        }
      },
      // brand相关
      brandListAll: [],
      brandName: "",
      curBrandPageNumber: 1,
      brandTotal: 0
    };
  },

  methods: {
    ...mapActions("root/productPicker", [
      "queryBrandList",
      "getCategoryTree",
      "queryAttrList",
      "getAttrInfo"
    ]),

    // 数据初始化
    dataInit() {
      let orgCode = this.form.orgCode || "default";

      // brand
      this.queryBrandList({ orgCode, pageNum: 1 })
        .then(({ list, total }) => {
          this.brandListAll = list;
          this.brandTotal = total;
        })
        .catch(err => {
          this.catgoryList = [];
        });

      // category
      this.getCategoryTree({ orgCode })
        .then(res => {
          this.catgoryList = res;
        })
        .catch(err => {
          this.catgoryList = [];
        });

      // attr
      this.queryAttr();
    },

    // org change
    onOrgChange({ orgCode }) {
      // clear
      this.$set(this.form, "brands", []);
      this.$set(this.form, "categories", []);
      this.$set(this.form, "attributes", []);

      // re-init
      this.dataInit(orgCode);
    },

    // 属性列表查询
    queryAttr() {
      const { orgCode = "default", attrType = "sku" } = this.form;
      const { skuAttrList, spuAttrList } = this;

      // 数据已存在 避免不必要的请求
      if (attrType === "sku" && skuAttrList.length) return;
      if (attrType === "spu" && spuAttrList.length) return;

      this.queryAttrList({
        orgCode,
        attrType
      });
    },

    // on attr change
    async onAttrChange(value, idx) {
      this.$set(this.form.attributes[idx], "values", []);

      // 通过判断是否已经请求过 以防止多余的请求开销
      if (value && !this.attrValuesMap[value]) {
        await this.queryAttrValueList([value], idx);
      }

      this.$forceUpdate();
    },

    // get attr list
    async queryAttrValueList(ids) {
      if (ids && ids.length) {
        await this.getAttrInfo({
          codeList: ids,
          orgCode: this.form.orgCode || "default"
        });
      }
    },

    // 新增一项attr
    addAttrItem() {
      // todo: max length 需要为可配置的
      if (this.form.attributes.length >= this.maxSKUAttributes) return;

      this.form.attributes.push({
        id: "",
        values: []
      });
    },

    // delete attr item
    deleteAttrItem(index) {
      this.form.attributes.splice(index, 1);
    },

    // 属性类型切换
    attributeTypeToggle(attrType) {
      this.$set(this.form, "attributes", []);

      this.queryAttr();
    },

    // 用于required的验证
    // 通过判断未选择商品 reject
    // 通过判断已选择了商品 resolve
    requiredValidator() {
      const { brands, categories, attributes } = this.form;
      if (!brands.length && !categories.length && !attributes.length) {
        return false;
      } else {
        return true;
      }
    },

    // todo: deep merge
    // customize data before submit
    dataCustomize(next) {
      next(Object.assign({}, this.form));
    },

    // hook: after data init
    async afterCompInit() {
      const { attributes } = this.form;

      this.queryAttr();

      if (attributes.length) {
        await this.queryAttrValueList(attributes.map(({ id }) => id));
        this.$forceUpdate();
      }
    },
    // start--brand 相关
    // 关闭下拉且之前有搜索字段时，清空操作
    visibleSelect(val) {
      if (!val && this.brandName) {
        this.brandName = "";
        this.brandListAll = [];
        this.curBrandPageNumber = 1;
        this.getBrandList();
      }
    },
    // 搜索
    // searchFn(query) {
    //   this.brandName = query;
    //   this.brandListAll = [];
    //   this.curBrandPageNumber = 1;
    //   this.getBrandList();
    // },
    // 滚动加载
    LoadMore() {
      if (this.brandTotal <= this.brandListAll.length) {
        return;
      }
      this.curBrandPageNumber++;
      this.getBrandList();
    },
    async getBrandList() {
      let orgCode = this.form.orgCode || "default";
      let { list, total } = await this.queryBrandList({
        orgCode,
        brandName: this.brandName,
        pageNum: this.curBrandPageNumber
      });
      this.brandListAll = [...this.brandListAll, ...list];
      this.brandTotal = total;
    }
    // end--brand 相关
  }
};
</script>

<style lang="scss">
.gt-productpicker-bycondition {
  &__attr-box {
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    padding: 12px;
  }

  &__attr-item {
    background: rgba(250, 250, 250, 1);
    padding: 8px;
    display: flex;
    margin-bottom: 8px;

    .el-form-item {
      margin-bottom: 0 !important;
    }

    .el-form-item:first-child .el-input {
      width: 176px;
    }

    .el-form-item:nth-child(2) {
      margin: 0 0px 0px 8px;
      .el-input {
        width: 375px;
      }
    }

    .el-icon-delete {
      color: rgba(0, 0, 0, 1);
      opacity: 0.35;
      cursor: pointer;
    }

    &--hidden {
      visibility: hidden;
    }

    .el-form-item__error {
      margin-left: 8px;
    }
  }

  .el-cascader-panel.is-bordered {
    overflow-x: auto;
  }

  .gt-org-tree__reference {
    width: 100%;
  }
  .productpicker-label-notes {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    .productpicker-label-notes-icon {
      line-height: 0;
      margin-left: 5px;
      cursor: pointer;
    }
  }
}
</style>
